import React, { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { AxiosError } from 'axios';
import environmentConfig from '../../config/environment-configs';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/Header';
import Container from '../../components/Container';
import Input from '../../components/Form/Input';
import Button from '../../components/Button';
import Title from '../../components/Title';
import Label from '../../components/Form/Label';
import { api } from '../../services/api';
import Modal from '../../components/Modal';
import { ModalB2BContainer, ModalBody } from '../../components/Modal/styles';
import { FieldSet } from '../../components/Form/FieldSet/styles';
import { LoadingSpinner } from '../../components/Loading/styles';
import { Actions, Categories, Labels } from '../../types/gtm';
import { scrollToErrors } from '../../util/scrollToErrors';

const ChangeEmailBox = styled.div`
  display: flex;
  justify-content: center;
  @media (max-width: 650px) {
    min-height: 435px;
    width: 100%;
    padding: 24px;
  }
  > form {
    height: 100%;
    display: flex;
    flex-direction: column;
    > button {
      margin-top: 32px;
    }
    @media (max-width: 650px) {
      width: 100%;
    }
  }
`;

const LinkKnowMore = styled.a`
  color: #666666;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
`;

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required('O e-mail é necessário para a alteração')
    .email('Insira um formato de e-mail válido')
    .max(100, 'O e-mail pode ter no máximo 100 caracteres.'),
});

const ChangeEmailPage = () => {
  const { authenticated, user, logout, authenticateOnAPI, homeInfo } =
    useAuth();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isAccountB2B, setAccountB2B] = useState<boolean>(false);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [modalB2BOpen, setModalB2BOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    window.dataLayer.push({
      event: 'customEventUserNonInteraction',
      event_category: Categories.EditUserEmail,
      event_action: Actions.Pageview,
      bd_suser_code: user?.globoId,
    });

    if (homeInfo?.componentAccountInfoB2B) {
      setAccountB2B(!!homeInfo?.componentAccountInfoB2B?.data?.globoId);
      setCanEdit(!homeInfo?.componentAccountInfoB2B?.data?.globoId);
    }
  }, [homeInfo]);

  const getRecaptchaToken = async (): Promise<string> => {
    return new Promise(resolve => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(environmentConfig.GOOGLE_RECAPTCHA_KEY, {
            action: 'emailMinhaConta',
          })
          .then((token: string) => {
            resolve(token);
          });
      });
    });
  };

  const submitChangeEmail = async (
    email: string,
    setFieldError: (field: string, message: string | undefined) => void,
  ) => {
    if (user.email === email) {
      window.dataLayer.push({
        event: 'customEventUserInteraction',
        event_category: Categories.EditUserEmail,
        event_action: Actions.Error,
        event_label: Labels.EmailNotChanged,
        bd_suser_code: user?.globoId,
      });
      toast.error('O e-mail não foi alterado.');
      return;
    }
    setLoading(true);
    const recaptchaToken = await getRecaptchaToken();

    const requestBody = {
      newEmail: email,
    };

    const token = await authenticateOnAPI();

    api
      .post('/user/email/change', requestBody, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Recaptcha-Token': recaptchaToken,
        },
      })
      .then(() => {
        setModalOpen(true);
        setLoading(false);
      })
      .catch((errorRequest: AxiosError) => {
        setLoading(false);
        let labelError: string | undefined = Labels.GenericError;
        switch (errorRequest.response?.status) {
          case 400:
            try {
              const body = errorRequest.response?.data;
              if (
                body &&
                typeof body === 'object' &&
                'error' in body &&
                typeof (body as any).error === 'string' &&
                ((body as any).error === 'recaptcha token is empty' ||
                  (body as any).error === 'Token problem')
              ) {
                toast.error(
                  'Ocorreu um erro inesperado. Atualize seu e-mail novamente.',
                );
              } else {
                labelError = Labels.EmailInvalid;
                setFieldError(
                  'email',
                  'Esse e-mail não é válido. Tente outro e-mail.',
                );
              }
            } catch (err) {
              labelError = Labels.EmailInvalid;
              setFieldError(
                'email',
                'Esse e-mail não é válido. Tente outro e-mail.',
              );
            }
            break;
          case 409:
            labelError = Labels.EmailInUse;
            setFieldError('email', 'Este e-mail já está sendo utilizado');
            break;
          default:
            toast.error(
              'Ocorreu um erro inesperado. Atualize seu e-mail novamente.',
            );
            break;
        }
        window.dataLayer.push({
          event: 'customEventUserInteraction',
          event_category: Categories.EditUserEmail,
          event_action: Actions.Error,
          event_label: labelError,
          bd_suser_code: user?.globoId,
        });
      });
  };

  interface ModalProps {
    email: string;
  }

  const ConfirmModal: React.FC<ModalProps> = ({ email }: ModalProps) => {
    useEffect(() => {
      window.dataLayer.push({
        event: 'customEventUserInteraction',
        event_category: Categories.EditUserEmail,
        event_action: Actions.PendingConfirmation,
        event_label: Labels.PendingConfirmationNotificationScreen,
        bd_suser_code: user?.globoId,
      });
    }, []);

    return (
      <Modal
        header="Confirmação de e-mail pendente"
        closeButton={() => {
          window.dataLayer.push({
            event: 'customEventUserInteraction',
            event_category: Categories.EditUserEmail,
            event_action: Actions.ClickClose,
            event_label: Labels.PendingConfirmationNotificationScreen,
            bd_suser_code: user?.globoId,
          });
          setModalOpen(false);
        }}
      >
        <ModalBody>
          <p>
            Olá, <b>{user.fullName}</b>.
          </p>
          <p>
            Para que a alteração de email seja realizada você precisa acessar
            seu email <b>{email}</b> e confirmar a mudança.
          </p>
        </ModalBody>
      </Modal>
    );
  };

  const B2BModal: React.FC = () => {
    useEffect(() => {
      window.dataLayer.push({
        event: 'customEventUserInteraction',
        event_category: Categories.EditUserEmail,
        event_action: Actions.ClickMore,
        event_label: Labels.B2BKnowMore,
        bd_suser_code: user?.globoId,
      });
    }, []);

    return (
      <Modal
        maxWidth="496px"
        contentWidth="100%"
        borderRadius="16px"
        customHeader="Como remover o vínculo e alterar o e-mail"
        closeButton={() => {
          window.dataLayer.push({
            event: 'customEventUserInteraction',
            event_category: Categories.EditUserEmail,
            event_action: Actions.ClickClose,
            event_label: Labels.B2BKnowMore,
            bd_suser_code: user?.globoId,
          });
          setModalB2BOpen(false);
        }}
      >
        <ModalB2BContainer>
          <p>
            <strong>
              Você só poderá alterar seu e-mail caso não esteja vinculado ao
              Globo Ads. Atenção: ao remover o vínculo, você perderá o acesso ao
              Globo Ads, por isso, recomendamos que não haja campanhas ativas
              antes de prosseguir.
            </strong>
          </p>
          <p>
            Após a desvinculação, será possível alterar seu e-mail nesta página.
            Para voltar a usar o Globo Ads, será necessário recadastrar seus
            dados profissionais ao fazer login.
          </p>
          <p>
            Para desvincular, envie um e-mail para{' '}
            <strong>globoads@g.globo</strong>:
          </p>
          <p>
            <i>Assunto</i>: Solicitação de desvinculação do Globo Ads para
            alteração do e-mail
          </p>
          <p>
            <i>Corpo do e-mail</i>:<br />
            Gostaria de desvincular minha conta do Globo Ads para alteração do
            e-mail.
            <br />
            E-mail: (exemplo@email.com)
            <br />
            CNPJ: (00.000.000/0000-00)
          </p>
        </ModalB2BContainer>
      </Modal>
    );
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        theme="colored"
        pauseOnHover={false}
      />

      {authenticated && (
        <>
          <Header
            screenMenu={Categories.EditUserEmail}
            user={user}
            onLogout={logout}
            showReturn
            onReturn={({ handleInternalReturn }) => {
              window.dataLayer.push({
                event: 'customEventUserInteraction',
                event_category: Categories.EditUserEmail,
                event_action: Actions.ClickBack,
                bd_suser_code: user?.globoId,
              });

              handleInternalReturn();
            }}
          />
          <Container>
            <ChangeEmailBox>
              <Formik
                enableReinitialize
                initialValues={{
                  email: user.email,
                }}
                validationSchema={validationSchema}
                onSubmit={(values, { setFieldError }) => {
                  submitChangeEmail(values.email, setFieldError);
                }}
              >
                {({ handleSubmit, handleChange, errors, values }) => {
                  return (
                    <>
                      {modalOpen && <ConfirmModal email={values.email} />}
                      {modalB2BOpen && <B2BModal />}
                      <form onSubmit={handleSubmit}>
                        <Title variant="page">Alterar e-mail</Title>
                        <FieldSet>
                          <Label htmlFor="email">Seu e-mail atual</Label>
                          <Input
                            readOnly={!canEdit}
                            data-testid="email-input"
                            onChange={handleChange}
                            value={values.email}
                            placeHolderText="Informe seu e-mail"
                            name="email"
                            errorMessage={errors.email ? errors.email : ''}
                            disableMessage={
                              isAccountB2B ? (
                                <>
                                  <span>
                                    Este e-mail está vinculado ao Globo Ads e
                                    para ser alterado, o vínculo precisa ser
                                    removido.{' '}
                                  </span>
                                  <LinkKnowMore
                                    onClick={() => setModalB2BOpen(true)}
                                  >
                                    Saiba mais
                                  </LinkKnowMore>
                                </>
                              ) : (
                                ''
                              )
                            }
                          />
                        </FieldSet>

                        {canEdit === true && (
                          <Button
                            onClick={() => {
                              window.dataLayer.push({
                                event: 'customEventUserInteraction',
                                event_category: Categories.EditUserEmail,
                                event_action: Actions.ClickSaveChanges,
                                bd_suser_code: user?.globoId,
                              });
                              scrollToErrors(errors);
                            }}
                            disabled={loading}
                            type="submit"
                            variant="primary"
                          >
                            {loading ? (
                              <LoadingSpinner isButton />
                            ) : (
                              'Salvar alterações'
                            )}
                          </Button>
                        )}
                      </form>
                    </>
                  );
                }}
              </Formik>
            </ChangeEmailBox>
          </Container>
        </>
      )}
    </>
  );
};

export default ChangeEmailPage;
